import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageModal from './LanguageModal';
import LogoModal from './LogoModal';
import './scrollbar.css';
import SEO from "../SEO";

// Create Theme Context
const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');

  const toggleTheme = () => {
    setTheme((prev) => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
      return newTheme;
    });
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const FLAGS = [
  { code: 'de', country: 'Germany', path: '/flag/germany.gif' },
  { code: 'it', country: 'Italy', path: '/flag/italy.gif' },
  { code: 'fr', country: 'France', path: '/flag/french.gif' },
  { code: 'es', country: 'Spain', path: '/flag/spain.gif' },
  { code: 'hk', country: 'Hong Kong', path: '/flag/hong-kong.gif' },
  { code: 'sg', country: 'Singapore', path: '/flag/singapore.gif' },
  { code: 'za', country: 'South Africa', path: '/flag/south-africa.gif' },
  { code: 'sa', country: 'Saudi Arabia', path: '/flag/saudi.gif' },
  { code: 'nl', country: 'Netherlands', path: '/flag/netherlands.gif' },
  { code: 'en', country: 'United Kingdom', path: '/flag/uk.gif' },
  { code: 'kr', country: 'South Korea', path: '/flag/south-korea.gif' },
  { code: 'jp', country: 'Japan', path: '/flag/japan.gif' },
];

const TRANSLATIONS = {
  de: {
    'Germany to You': 'Deutschland zu Ihnen',
    'Crafted Excellence': 'Gefertigte Exzellenz',
    'worldwidecraft': 'Weltweite Handwerkskunst',
    'Contact': 'Global Verbinden',
  },
  en: {
    'Germany to You': 'Germany to You',
    'Crafted Excellence': 'Crafted Excellence',
    'worldwidecraft': 'Worldwide Craft',
    'Contact': 'Connect Globally',
  },
  es: {
    'Germany to You': 'Alemania para Ti',
    'Crafted Excellence': 'Excelencia Artesanal',
    'worldwidecraft': 'Artesanía Mundial',
    'Contact': 'Conectar Globalmente',
  },
  fr: {
    'Germany to You': 'Allemagne pour Vous',
    'Crafted Excellence': 'Excellence Artisanale',
    'worldwidecraft': 'Artisanat Mondial',
    'Contact': 'Connexion Mondiale',
  },
  it: {
    'Germany to You': 'Germania per Te',
    'Crafted Excellence': 'Eccellenza Artigianale',
    'worldwidecraft': 'Artigianato Mondiale',
    'Contact': 'Connessione Globale',
  },
  nl: {
    'Germany to You': 'Duitsland voor Jou',
    'Crafted Excellence': 'Vakmanschap Excellentie',
    'worldwidecraft': 'Wereldwijd Vakmanschap',
    'Contact': 'Wereldwijd Verbinden',
  },
  sa: {
    'Germany to You': 'ألمانيا إليك',
    'Crafted Excellence': 'التميز الحرفي',
    'worldwidecraft': 'الحرفية العالمية',
    'Contact': 'الاتصال عالميًا',
  },
  hk: {
    'Germany to You': '德國給您',
    'Crafted Excellence': '精湛卓越',
    'worldwidecraft': '全球工藝',
    'Contact': '全球聯繫',
  },
  sg: {
    'Germany to You': '德国给您',
    'Crafted Excellence': '精湛卓越',
    'worldwidecraft': '全球工艺',
    'Contact': '全球联系',
  },
  za: {
    'Germany to You': 'Duitsland vir Jou',
    'Crafted Excellence': 'Vakmanskap Uitnemendheid',
    'worldwidecraft': 'Wêreldwye Vakmanskap',
    'Contact': 'Wêreldwyd Verbind',
  },
  kr: {
    'Germany to You': '당신에게 독일',
    'Crafted Excellence': '정교한 우수성',
    'worldwidecraft': '세계적인 공예',
    'Contact': '글로벌 연결',
  },
  jp: {
    'Germany to You': 'あなたへのドイツ',
    'Crafted Excellence': '卓越した職人技',
    'worldwidecraft': '世界的なクラフト',
    'Contact': 'グローバルに接続',
  },
};

const NAVIGATION_ITEMS = [
  { path: '/', label: 'Germany to You' },
  { path: '/craftedexcellence', label: 'Crafted Excellence' },
  { path: '/worldwidecraft', label: 'worldwidecraft' },
  { path: '/contactform', label: 'Contact' },
];

const FlagButton = ({ onClick }) => {
  const [currentFlagIndex, setCurrentFlagIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFlagIndex((prevIndex) =>
        prevIndex === FLAGS.length - 1 ? 0 : prevIndex + 1
      );
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <button
      onClick={onClick}
      className="flex items-center justify-center h-8 md:h-8 transition-all duration-200"
      type="button"
    >
      <img
        src={FLAGS[currentFlagIndex].path}
        alt={FLAGS[currentFlagIndex].country}
        className="h-full w-auto hover:opacity-80"
      />
    </button>
  );
};

const ThemeToggleButton = () => {
  const { theme, toggleTheme } = useContext(ThemeContext);

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? (
        <Moon className="h-6 w-6 text-gray-800" />
      ) : (
        <Sun className="h-6 w-6 text-yellow-400" />
      )}
    </button>
  );
};

const MobileMenu = ({ isOpen, onClose, location, navigate, getButtonStyles, language }) => {
  const { theme } = useContext(ThemeContext);

  return (
    <div
      className={`fixed inset-0 z-50 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-black'}`}
    >
      <SEO
        title="Made in Germany – High-Quality German Products for Global Markets"
        description="Explore the best Made in Germany products. Connect with top German manufacturers and suppliers for premium quality goods."
        keywords="Made in Germany, German products, German manufacturers, premium Gassmannquality, German engineering, B2B products"
        url="https://made-in-germany.uk"
        image="https://made-in-germany.uk/static/images/hero-image.webp"
      />
      <div className="flex flex-col h-full p-4">
        <div className="flex justify-end mb-6">
          <button onClick={onClose} className="p-2">
            <X className="h-8 w-8" />
          </button>
        </div>
        <div className="flex flex-col gap-4">
          {NAVIGATION_ITEMS.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`${getButtonStyles(item.path)} w-full text-center py-4`}
              onClick={() => {
                navigate(item.path);
                onClose();
              }}
            >
              {TRANSLATIONS[language][item.label]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Header = ({ onLanguageChange }) => {
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useContext(ThemeContext);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const handleLanguageModalToggle = () => setShowLanguageModal((prev) => !prev);

  const handleLogoHover = () => {
    if (window.innerWidth >= 768) {
      setShowLogoModal(true);
    }
  };

  const handleLogoClick = () => {
    if (window.innerWidth < 768) {
      setShowLogoModal(true);
    }
    navigate('/');
  };

  const handleCloseLogoModal = () => setShowLogoModal(false);

  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  const handleSelectLanguage = (languageCode) => {
    setCurrentLanguage(languageCode);
    onLanguageChange(languageCode);
    setShowLanguageModal(false);
  };

  const getButtonStyles = (path) => {
    const isActive = location.pathname === path;
    return `px-4 py-2 rounded-[5px] font-medium transition-all duration-200 ${
      isActive
        ? theme === 'dark'
          ? 'bg-gray-700 text-white hover:shadow-[0px_0px_15px_5px_#1F2937]'
          : 'bg-[#0B111F] text-white hover:shadow-[0px_0px_15px_5px_#0B111F]'
        : theme === 'dark'
        ? 'bg-gray-800 text-white hover:bg-gray-600 hover:shadow-[0px_0px_15px_5px_#1F2937]'
        : 'bg-white text-black hover:bg-[#0B111F] hover:text-white hover:shadow-[0px_0px_15px_5px_#0B111F]'
    }`;
  };

  return (
    <>
      <header
        className={`w-full z-40 transition-all duration-300 ${
          isScrolled ? 'fixed top-0 left-0 right-0 shadow-lg' : 'relative'
        } ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-black'}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div
              className="relative cursor-pointer"
              onMouseEnter={handleLogoHover}
              onClick={handleLogoClick}
            >
              <img src="/made-in-germany-logo.webp" alt="Made-In-Germany" className="h-8 md:h-12" />
            </div>
            <div className="flex items-center gap-6 md:gap-6">
              <div className="hidden md:flex items-center gap-2">
                {NAVIGATION_ITEMS.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    className={getButtonStyles(item.path)}
                    onClick={() => navigate(item.path)}
                  >
                    {TRANSLATIONS[currentLanguage][item.label]}
                  </button>
                ))}
              </div>
              <FlagButton onClick={handleLanguageModalToggle} />
              <ThemeToggleButton />
              <button
                type="button"
                className={`md:hidden p-2 rounded-lg transition-all duration-200 ${
                  theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                onClick={toggleMobileMenu}
              >
                <Menu className="h-8 w-8" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        location={location}
        navigate={navigate}
        getButtonStyles={getButtonStyles}
        language={currentLanguage}
      />

      <LogoModal 
        isVisible={showLogoModal} 
        onClose={handleCloseLogoModal} 
        language={currentLanguage} 
        isScrolled={isScrolled}
      />

      {showLanguageModal && (
        <LanguageModal
          onClose={handleLanguageModalToggle}
          onSelectLanguage={handleSelectLanguage}
        />
      )}
    </>
  );
};

export default Header;